// BookingContext — single source of truth for the entire booking
// enquiry flow across the site. Used by:
//   • HomeBookingWidget (homepage + any future surface)
//   • /booking page (full editorial flow)
//   • StickySummary (floating enquiry recap)
//   • "Choose your house" card grid on /booking
//
// State is persisted to localStorage so a visitor can start an enquiry
// on the homepage, navigate to /booking, /hotels/:slug or anywhere
// else, and resume exactly where they left off — including on a hard
// refresh. The submission helper (submit()) is the single payload
// builder for the entire site so the same enquiry-email is generated
// regardless of which surface the user clicks "Send Inquiry" from.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { HOTELS } from "@/lib/data";
import { getCampLabels, getCampLabel } from "@/lib/bookingCamps";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "xaluca.bookingEnquiry.v1";

// Default room shape — shared so every new property/room mutation
// starts from the same baseline (avoids "undefined adults" type bugs).
export const DEFAULT_ROOM = Object.freeze({
  room_type: "",
  adults: 2,
  children: 0,
  infants: 0,
  board_basis: "breakfast",
});

export const DEFAULT_HOTEL_DETAIL = Object.freeze({
  check_in: "",
  check_out: "",
  notes: "",
  rooms: [{ ...DEFAULT_ROOM }],
});

export const DEFAULT_SERVICE_DETAIL = Object.freeze({
  date: "",
  time: "",
  people: 2,
  duration: "",
  pickup: "",
  requirements: "",
  notes: "",
});

const DEFAULT_FORM = Object.freeze({
  full_name: "",
  email: "",
  phone: "",
  whatsapp: "",
  check_in: "",
  check_out: "",
  adults: 2,
  children: 0,
  rooms: 1,
  room_preference: "",
  transport_needs: "",
  special_occasion: "",
  dietary: "",
  message: "",
});

// Picks a localised label out of an {en, fr, es} dictionary.
const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// ── Hydration ───────────────────────────────────────────────────────
// Pulls the previously-saved enquiry from localStorage. Robust to
// malformed JSON / missing fields by deep-merging with the defaults.
const hydrateFromStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const BookingContext = createContext(null);

export const BookingProvider = ({ children }) => {
  const { lang } = useLanguage();

  // Hydrate state from localStorage on first mount so a visitor's
  // partial enquiry survives navigation + hard refreshes.
  const initial = hydrateFromStorage();
  const [selectedHotels, setSelectedHotels] = useState(() => initial?.selectedHotels || {});
  const [selectedServices, setSelectedServices] = useState(() => initial?.selectedServices || {});
  const [form, setForm] = useState(() => ({ ...DEFAULT_FORM, ...(initial?.form || {}) }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Shared Clear-All confirmation flow — both the booking widget and
  // the floating summary call `requestClearAll()` to open the same
  // dialog; `confirmClearAll()` performs the actual wipe via the
  // existing `clearAll()` reducer; `cancelClearAll()` closes the
  // dialog without side effects. The dialog UI lives in a tiny
  // top-level component (`ClearAllConfirmDialog`) mounted globally
  // in App.js so the logic is never duplicated.
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const requestClearAll = useCallback(() => setClearConfirmOpen(true), []);
  const cancelClearAll = useCallback(() => setClearConfirmOpen(false), []);
  // Progressive-flow step shared across the booking widget and the
  // floating enquiry summary. 1 = Accommodation, 2 = Extras,
  // 3 = Contact details + Send. Persisted so that a returning visitor
  // resumes at the step they had progressed to (subject to step
  // requirements — if the enquiry is empty we always render step 1).
  const [flowStep, setFlowStepRaw] = useState(() => {
    const n = Number(initial?.flowStep) || 1;
    return n >= 1 && n <= 3 ? n : 1;
  });
  // Guarded setter — step number must stay within bounds and may only
  // advance past 1 if there is at least one item in the enquiry.
  const setFlowStep = useCallback(
    (n) => {
      const next = Math.max(1, Math.min(3, Number(n) || 1));
      setFlowStepRaw(next);
    },
    []
  );

  // Persist on every meaningful change. fieldErrors and isSubmitting
  // are transient and never persisted.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const payload = { selectedHotels, selectedServices, form, flowStep };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage quota errors are non-fatal — silently ignore.
    }
  }, [selectedHotels, selectedServices, form, flowStep]);

  // ── Hotel mutations ────────────────────────────────────────────────
  const toggleHotel = useCallback((slug) => {
    setSelectedHotels((prev) => {
      if (prev[slug]) {
        const next = { ...prev };
        delete next[slug];
        return next;
      }
      return { ...prev, [slug]: { ...DEFAULT_HOTEL_DETAIL, rooms: [{ ...DEFAULT_ROOM }] } };
    });
  }, []);

  // Toggle with toast feedback. Use this from card grids / planner
  // surfaces so the user gets a non-intrusive confirmation.
  const toggleHotelWithFeedback = useCallback(
    (slug) => {
      const isAdding = !selectedHotels[slug];
      toggleHotel(slug);
      const hotel = HOTELS.find((h) => h.slug === slug);
      const name = hotel?.name || pick({ en: "Property", fr: "Adresse", es: "Propiedad" }, lang);
      if (isAdding) {
        toast.success(
          pick(
            {
              en: "{name} added to your enquiry",
              fr: "{name} ajouté à votre demande",
              es: "{name} añadido a tu solicitud",
            },
            lang
          ).replace("{name}", name)
        );
      } else {
        toast(
          pick(
            {
              en: "{name} removed from your enquiry",
              fr: "{name} retiré de votre demande",
              es: "{name} eliminado de tu solicitud",
            },
            lang
          ).replace("{name}", name)
        );
      }
    },
    [selectedHotels, toggleHotel, lang]
  );

  const updateHotelDetail = useCallback((slug, key, value) => {
    setSelectedHotels((prev) => ({
      ...prev,
      [slug]: { ...(prev[slug] || DEFAULT_HOTEL_DETAIL), [key]: value },
    }));
  }, []);

  // Replace the whole detail object for a property — used when the
  // widget mutates the rooms[] array in a single call.
  const replaceHotelDetail = useCallback((slug, detail) => {
    setSelectedHotels((prev) => ({ ...prev, [slug]: detail }));
  }, []);

  // ── Service mutations ──────────────────────────────────────────────
  const toggleService = useCallback((key) => {
    setSelectedServices((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { ...DEFAULT_SERVICE_DETAIL } };
    });
  }, []);

  const updateServiceDetail = useCallback((key, field, value) => {
    setSelectedServices((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || DEFAULT_SERVICE_DETAIL), [field]: value },
    }));
  }, []);

  // ── Form mutations ─────────────────────────────────────────────────
  const setFormField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      // Auto-clear an inline error the moment the user types a
      // non-empty trimmed string into a previously-flagged field.
      if (!prev[key]) return prev;
      if (typeof value !== "string" || value.trim().length === 0) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearFieldError = useCallback((key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // ── Reset ─────────────────────────────────────────────────────────
  const clearAll = useCallback(() => {
    setSelectedHotels({});
    setSelectedServices({});
    setForm({ ...DEFAULT_FORM });
    setFieldErrors({});
    setFlowStepRaw(1);
  }, []);
  // Performs the actual wipe + closes the confirmation dialog. This is
  // the *single* shared reset handler the user requested — both the
  // widget's "Clear all" button and the floating summary's repurposed
  // X button funnel through it via `requestClearAll()`.
  const confirmClearAll = useCallback(() => {
    setClearConfirmOpen(false);
    clearAll();
  }, [clearAll]);

  // ── Derived helpers ────────────────────────────────────────────────
  const selectedHotelSlugs = useMemo(() => Object.keys(selectedHotels), [selectedHotels]);
  const selectedServiceKeys = useMemo(() => Object.keys(selectedServices), [selectedServices]);
  const totalItems = selectedHotelSlugs.length + selectedServiceKeys.length;

  // ── Per-step validators ────────────────────────────────────────────
  // Returns true if the user has at least one accommodation in their
  // enquiry. Step 1 cannot be left until this is true.
  const isStep1Complete = selectedHotelSlugs.length > 0 || selectedServiceKeys.length > 0;

  // Validates the contact-details step. Returns the error map (empty
  // if valid). Re-usable by both the widget and the /booking page.
  const validateContactDetails = useCallback(() => {
    const errs = {};
    const nameVal = (form.full_name || "").trim();
    const emailVal = (form.email || "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
    if (!nameVal) {
      errs.full_name = pick(
        {
          en: "Please enter your full name",
          fr: "Veuillez saisir votre nom complet",
          es: "Por favor, introduce tu nombre completo",
        },
        lang
      );
    }
    if (!emailVal) {
      errs.email = pick(
        {
          en: "Please enter your email address",
          fr: "Veuillez saisir votre adresse e-mail",
          es: "Por favor, introduce tu correo electrónico",
        },
        lang
      );
    } else if (!emailOk) {
      errs.email = pick(
        {
          en: "Please enter a valid email address",
          fr: "Veuillez saisir une adresse e-mail valide",
          es: "Por favor, introduzca un correo electrónico válido",
        },
        lang
      );
    }
    return errs;
  }, [form.full_name, form.email, lang]);

  // ── Submit ─────────────────────────────────────────────────────────
  // Builds the payload from the unified state and POSTs to the same
  // /api/multi-booking-requests endpoint regardless of which surface
  // triggered the submission. `source` identifies where the user
  // clicked Send so the host can see the conversion funnel.
  const submit = useCallback(
    async ({ source = "widget", serviceCatalogue = [] } = {}) => {
      if (selectedHotelSlugs.length === 0 && selectedServiceKeys.length === 0) {
        toast.error(
          pick(
            {
              en: "Please add at least one stay or experience to your enquiry.",
              fr: "Ajoutez au moins un séjour ou une expérience à votre demande.",
              es: "Añade al menos una estancia o experiencia a tu solicitud.",
            },
            lang
          )
        );
        return { ok: false, reason: "empty" };
      }
      const errs = validateContactDetails();
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        return { ok: false, reason: "validation", errors: errs };
      }
      // Clear any stale errors on a clean submit.
      if (Object.keys(fieldErrors).length > 0) setFieldErrors({});

      const fallbackCheckIn = form.check_in || new Date().toISOString().slice(0, 10);
      const fallbackCheckOut = form.check_out || fallbackCheckIn;
      const items = [];

      selectedHotelSlugs.forEach((slug) => {
        const hotel = HOTELS.find((h) => h.slug === slug);
        const det = selectedHotels[slug] || DEFAULT_HOTEL_DETAIL;

        // Camp-parent property with per-camp bookings — each camp is
        // its own independent stay with its own dates / rooms / notes.
        // Emit ONE item per (camp × room) so the enquiry email lays
        // each camp out clearly.
        if (Array.isArray(det.campBookings) && det.campBookings.length > 0) {
          det.campBookings.forEach((cb) => {
            const campLabel = getCampLabel(slug, cb.campSlug, "en");
            const campSuffix = campLabel ? ` — ${campLabel}` : "";
            const rooms =
              Array.isArray(cb.rooms) && cb.rooms.length ? cb.rooms : [{ ...DEFAULT_ROOM }];
            rooms.forEach((room, idx) => {
              const totalChildren = (Number(room.children) || 0) + (Number(room.infants) || 0);
              items.push({
                hotel: hotel
                  ? `${hotel.name}${campSuffix}${rooms.length > 1 ? ` · Room ${idx + 1}` : ""}`
                  : slug,
                check_in: cb.check_in || fallbackCheckIn,
                check_out: cb.check_out || cb.check_in || fallbackCheckOut,
                adults: Number(room.adults) || 2,
                children: totalChildren,
                rooms: 1,
                room_type: room.room_type || null,
                board_basis: room.board_basis || null,
                special_requests: idx === 0 ? cb.notes || null : null,
                kind: "hotel",
              });
            });
          });
          return;
        }

        const campSlugs = Array.isArray(det.campSlugs)
          ? det.campSlugs
          : det.campSlug ? [det.campSlug] : [];
        const campLabels = getCampLabels(slug, campSlugs, "en");
        const campSuffix = campLabels.length > 0 ? ` — ${campLabels.join(" · ")}` : "";
        const rooms =
          Array.isArray(det.rooms) && det.rooms.length ? det.rooms : [{ ...DEFAULT_ROOM }];
        rooms.forEach((room, idx) => {
          const totalChildren = (Number(room.children) || 0) + (Number(room.infants) || 0);
          items.push({
            hotel: hotel
              ? `${hotel.name}${campSuffix}${rooms.length > 1 ? ` · Room ${idx + 1}` : ""}`
              : slug,
            check_in: det.check_in || fallbackCheckIn,
            check_out: det.check_out || det.check_in || fallbackCheckOut,
            adults: Number(room.adults) || 2,
            children: totalChildren,
            rooms: 1,
            room_type: room.room_type || null,
            board_basis: room.board_basis || null,
            special_requests: idx === 0 ? det.notes || null : null,
            kind: "hotel",
          });
        });
      });

      selectedServiceKeys.forEach((key) => {
        const svc = serviceCatalogue.find((s) => s.key === key);
        const det = selectedServices[key] || DEFAULT_SERVICE_DETAIL;
        const dateStr = det.date || fallbackCheckIn;
        const requestNotes = [
          det.time && `Time: ${det.time}`,
          det.duration && `Duration: ${det.duration}`,
          det.pickup && `Pickup/drop-off: ${det.pickup}`,
          det.requirements && `Requirements: ${det.requirements}`,
          det.notes && `Notes: ${det.notes}`,
        ]
          .filter(Boolean)
          .join(" · ");
        items.push({
          hotel: svc ? pick(svc.title, "en") : key,
          check_in: dateStr,
          check_out: dateStr,
          adults: Number(det.people) || 2,
          children: 0,
          rooms: 0,
          special_requests: requestNotes || null,
          kind: "service",
          service_key: key,
          service_label: svc ? pick(svc.title, "en") : key,
        });
      });

      setIsSubmitting(true);
      try {
        await axios.post(`${API}/multi-booking-requests`, {
          full_name: form.full_name,
          email: form.email,
          phone: form.phone || null,
          items,
          message: form.message || null,
          language: lang,
          room_preference: form.room_preference || null,
          transport_needs: form.transport_needs || null,
          special_occasion: form.special_occasion || null,
          dietary: form.dietary || null,
          whatsapp: form.whatsapp || null,
          source,
        });
        toast.success(
          pick(
            {
              en: "Thank you — a host will be in touch within 24 hours.",
              fr: "Merci — un hôte vous répond sous 24 heures.",
              es: "Gracias — un anfitrión le responderá en 24 horas.",
            },
            lang
          )
        );
        clearAll();
        return { ok: true };
      } catch (err) {
        toast.error(
          pick(
            {
              en: "We couldn't send the enquiry. Please try again.",
              fr: "Nous n'avons pas pu envoyer la demande. Réessayez.",
              es: "No pudimos enviar la solicitud. Inténtelo de nuevo.",
            },
            lang
          )
        );
        return { ok: false, reason: "network" };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      selectedHotelSlugs,
      selectedServiceKeys,
      selectedHotels,
      selectedServices,
      form,
      lang,
      validateContactDetails,
      fieldErrors,
      clearAll,
    ]
  );

  const value = useMemo(
    () => ({
      // state
      selectedHotels,
      selectedServices,
      form,
      fieldErrors,
      isSubmitting,
      flowStep,
      // derived
      selectedHotelSlugs,
      selectedServiceKeys,
      totalItems,
      isStep1Complete,
      // hotel actions
      toggleHotel,
      toggleHotelWithFeedback,
      updateHotelDetail,
      replaceHotelDetail,
      setSelectedHotels,
      // service actions
      toggleService,
      updateServiceDetail,
      setSelectedServices,
      // form actions
      setForm,
      setFormField,
      // flow actions
      setFlowStep,
      // validation
      fieldErrorsSet: setFieldErrors,
      clearFieldError,
      validateContactDetails,
      // global
      clearAll,
      submit,
      // clear-all confirmation flow (shared between widget + summary)
      clearConfirmOpen,
      requestClearAll,
      cancelClearAll,
      confirmClearAll,
    }),
    [
      selectedHotels,
      selectedServices,
      form,
      fieldErrors,
      isSubmitting,
      flowStep,
      selectedHotelSlugs,
      selectedServiceKeys,
      totalItems,
      isStep1Complete,
      toggleHotel,
      toggleHotelWithFeedback,
      updateHotelDetail,
      replaceHotelDetail,
      toggleService,
      updateServiceDetail,
      setFormField,
      setFlowStep,
      clearFieldError,
      validateContactDetails,
      clearAll,
      submit,
      clearConfirmOpen,
      requestClearAll,
      cancelClearAll,
      confirmClearAll,
    ]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error("useBooking must be used inside <BookingProvider>");
  }
  return ctx;
};
